import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import WelcomePage from './components/WelcomePage';
import AdminDashboard from './components/AdminDashboard';
import AdminInfo from './components/AdminInfo';
import TeamsList from './components/TeamsList';
import ProtectedRoute from './components/ProtectedRoute';
import './index.css';

// API endpoint - change this to your server URL
// don't change this to localhost
const API_URL = import.meta.env.VITE_API_URL || '';

function App() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [telegramReady, setTelegramReady] = useState(false);

    useEffect(() => {
        // Initialize Telegram WebApp
        if (window.Telegram && window.Telegram.WebApp) {
            const tg = window.Telegram.WebApp;
            tg.ready();
            tg.expand();
            setTelegramReady(true);
        } else {
            console.warn('Telegram WebApp not available. Running in standalone mode.');
            setTelegramReady(true);
        }

        // Authenticate user
        authenticateUser();
    }, []);

    async function authenticateUser() {
        try {
            setLoading(true);
            setError(null);

            // Get initData from Telegram WebApp
            const initData = window.Telegram?.WebApp?.initData;

            if (!initData) {
                // If no initData (running outside Telegram), show demo mode
                console.warn('No initData available. Running in demo mode.');
                setError('This app must be opened from Telegram. initData is required for authentication.');
                setLoading(false);
                return;
            }

            // Send to backend for validation
            const response = await fetch(`${API_URL}/api/auth`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ initData }),
            });

            const data = await response.json();

            if (data.success) {
                // Handle both response formats (data.user or data.data.user)
                const userData = data.data?.user || data.user;
                setUser(userData);
                console.log('✅ User authenticated:', userData);
            } else {
                setError(data.error || 'Authentication failed');
                console.error('❌ Authentication failed:', data.error);
            }
        } catch (error) {
            console.error('❌ Auth error:', error);
            setError(error.message || 'Failed to connect to server. Make sure the server is running on ' + API_URL);
        } finally {
            setLoading(false);
        }
    }

    if (!telegramReady) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center text-gray-500">جاري التهيئة...</div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="max-w-2xl mx-auto">
                <div className="text-center py-10 text-gray-500">🔐 جاري التحقق من الهوية...</div>
            </div>
        );
    }

    if (error && !user) {
        return (
            <div className="max-w-2xl mx-auto">
                <div className="bg-gray-50 rounded-xl p-5 mb-4 shadow-md">
                    <h1 className="text-2xl font-bold mb-4 text-gray-900">⚠️ خطأ في التحقق من الهوية</h1>
                    <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">{error}</div>
                    <button className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors" onClick={authenticateUser}>
                        إعادة المحاولة
                    </button>
                    <div className="mt-4 text-sm text-gray-600 text-right">
                        <p className="font-semibold mb-2">ملاحظة: يجب فتح التطبيق من تيليجرام للعمل بشكل صحيح.</p>
                        <p className="mt-2 mb-2">إذا كنت تختبر محلياً، تأكد من:</p>
                        <ul className="list-disc list-inside space-y-1">
                            <li>الخادم يعمل على {API_URL}</li>
                            <li>أنك تفتح التطبيق من تيليجرام</li>
                            <li>أن BOT_TOKEN مضبوط بشكل صحيح في ملف .env</li>
                        </ul>
                    </div>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="max-w-2xl mx-auto">
                <div className="bg-gray-50 rounded-xl p-5 shadow-md">
                    <h1 className="text-2xl font-bold mb-4 text-gray-900">لا توجد بيانات مستخدم</h1>
                    <button className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors" onClick={authenticateUser}>
                        إعادة المحاولة
                    </button>
                </div>
            </div>
        );
    }

    return (
        <BrowserRouter>
            <div className="min-h-screen bg-gray-50">
                <Navbar user={user} />
                <Routes>
                    {/* Public Route - Welcome Page (accessible to all) */}
                    <Route
                        path="/"
                        element={<WelcomePage />}
                    />

                    {/* Protected Admin Routes */}
                    <Route
                        path="/admin"
                        element={
                            <ProtectedRoute user={user}>
                                <AdminDashboard user={user} />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/teams"
                        element={
                            <ProtectedRoute user={user}>
                                <TeamsList user={user} />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/info"
                        element={
                            <ProtectedRoute user={user}>
                                <AdminInfo user={user} onRefresh={authenticateUser} />
                            </ProtectedRoute>
                        }
                    />

                    {/* Catch all - redirect to home */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </div>
        </BrowserRouter>
    );
}

export default App;
